/**
 * Firestore Storage Service
 * 
 * Implementation using Firebase Firestore for player data persistence.
 * Requires authentication to be set up.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { StorageService, PlayerStateData } from './StorageService';
import { User } from 'firebase/auth';

export class FirestoreStorageService implements StorageService {
  private user: User | null;

  constructor(user: User | null) {
    this.user = user;
  }

  isAvailable(): boolean {
    return this.user !== null;
  }

  async loadPlayerState(): Promise<PlayerStateData | null> {
    if (!this.user) {
      throw new Error('User not authenticated');
    }

    try {
      const playerRef = doc(db, 'players', this.user.uid);
      const docSnap = await getDoc(playerRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          player: data.player || data, // Handle both formats
          position: data.position || { q: 0, r: 0 },
          exploredHexes: data.exploredHexes || [],
          travelState: data.travelState || null,
          // Sync Fields
          combatState: data.combatState || null,
          pendingEncounter: data.pendingEncounter || null,
          currentStory: data.currentStory || null,
          activeQuestId: data.activeQuestId || null,
          questState: data.questState || null,
        };
      }
    } catch (error) {
      console.error('Failed to load player from Firestore:', error);
      throw error;
    }

    return null;
  }

  async savePlayerState(state: PlayerStateData): Promise<void> {
    if (!this.user) {
      throw new Error('User not authenticated');
    }

    try {
      const playerRef = doc(db, 'players', this.user.uid);
      
      const dataToSave = {
        ...state.player,
        position: state.position,
        exploredHexes: state.exploredHexes,
        travelState: state.travelState,
        // Sync Fields
        combatState: state.combatState,
        pendingEncounter: state.pendingEncounter,
        activeQuestId: state.activeQuestId,
        questState: state.questState,
        updatedAt: serverTimestamp(),
      };
      
      // DEBUG: Log when saving hasSeenOnboarding
      if (state.player.hasSeenOnboarding !== undefined) {
        console.log('💾 Saving hasSeenOnboarding to Firestore:', state.player.hasSeenOnboarding);
      }
      
      await setDoc(playerRef, dataToSave, { merge: true });
    } catch (error) {
      console.error('Failed to save player to Firestore:', error);
      throw error;
    }
  }
}

